<template>
  <b-modal
    ref="budgetModal"
    title="Thông tin ngân sách"
    size="lg"
    ok-only
    ok-title="Đóng"
    ok-variant="secondary"
  >
    <div class="pr-budget-header">
      <div class="pr-budget-level">
        <span class="pr-budget-level-tag">{{ budgetLevelTag }}</span>
        <span class="text-muted">{{ budgetLevelDetailText }}</span>
      </div>
    </div>

    <b-row class="no-gutters pr-budget-kpi-row">
      <b-col cols="4" class="pr-2">
        <div class="pr-budget-kpi-box">
          <div class="text-muted">Phân bổ</div>
          <div class="pr-budget-kpi-big">
            {{ formatShortVnd(effectiveAllocated) }}
          </div>
          <div class="pr-budget-kpi-sub">
            {{ formatVnd(effectiveAllocated) }} đ
          </div>
        </div>
      </b-col>
      <b-col cols="4" class="px-2">
        <div class="pr-budget-kpi-box">
          <div class="text-muted">Đã sử dụng</div>
          <div class="pr-budget-kpi-big">
            {{ formatShortVnd(effectiveUsedAmount) }}
          </div>
          <div class="pr-budget-kpi-sub">
            {{ computedBudgetUsedPercent }}% phân bổ
          </div>
        </div>
      </b-col>
      <b-col cols="4" class="pl-2">
        <div
          class="pr-budget-kpi-box"
          :class="effectiveRemainingAmount < 0 ? 'pr-budget-kpi-box--remaining-danger' : 'pr-budget-kpi-box--remaining'"
        >
          <div class="text-muted">Còn lại</div>
          <div
            class="pr-budget-kpi-big"
            :class="effectiveRemainingAmount < 0 ? 'text-danger' : 'text-success'"
          >
            {{ formatShortVnd(effectiveRemainingAmount) }}
          </div>
          <div class="pr-budget-kpi-sub">
            {{ formatVnd(effectiveRemainingAmount) }} đ
          </div>
        </div>
      </b-col>
    </b-row>

    <div class="mt-3 pr-budget-progress-label">
      <span class="text-muted">
        {{ formatShortVnd(effectiveUsedAmount) }}/{{
          formatShortVnd(effectiveAllocated)
        }}
      </span>
      <span class="pr-budget-progress-percent">{{ computedBudgetUsedPercent }}%</span>
    </div>

    <b-progress
      :value="computedBudgetUsedPercent"
      :max="100"
      height="8px"
      variant="success"
      class="mt-1"
    />

    <div class="mt-3">
      <b-alert
        v-for="(a, idx) in visibleAlerts"
        :key="`${a.code || 'ALERT'}-${idx}`"
        show
        :variant="a.variant || 'warning'"
        class="pr-budget-alert mb-2"
      >
        <i class="fas fa-exclamation-triangle"></i>
        <div>{{ a.message }}</div>
      </b-alert>
    </div>

    <hr class="my-4" />
    <div class="pr-budget-snapshots">
      <b-row>
        <b-col cols="4" class="text-center">
          <div class="text-muted">Còn lại sau PR</div>
          <div
            class="pr-budget-snapshots-amount"
            :class="effectiveRemainingAfterPR < 0 ? 'text-danger' : ''"
          >
            {{
              effectiveRemainingAfterPR != null
                ? convertPrice(effectiveRemainingAfterPR)
                : '--'
            }}
          </div>
        </b-col>
        <b-col cols="4" class="text-center">
          <div class="text-muted">Ngân sách tháng</div>
          <div class="pr-budget-snapshots-amount">
            {{
              effectiveRemainingAmount !== null
                ? convertPrice(effectiveRemainingAmount)
                : '--'
            }}
          </div>
        </b-col>
        <b-col cols="4" class="text-center">
          <div class="text-muted">Ngân sách năm</div>
          <div class="pr-budget-snapshots-amount">
            {{
              pr.budgetYearRemainingAmount != null
                ? convertPrice(pr.budgetYearRemainingAmount)
                : '--'
            }}
          </div>
        </b-col>
      </b-row>
    </div>
  </b-modal>
</template>

<script>
import { convertPrice } from '@/utils/common';

export default {
  name: 'PurchaseRequestBudgetModal',
  props: {
    pr: {
      type: Object,
      default: () => ({}),
    },
    line: {
      type: Object,
      default: null,
    },
    budgetLevelTag: {
      type: String,
      default: '',
    },
    budgetLevelDetailText: {
      type: String,
      default: '',
    },
    budgetUsedPercent: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    effectiveAllocated() {
      return this.line?.budgetAllocatedAmount ?? this.pr.budgetAllocatedAmount;
    },
    effectiveUsedAmount() {
      // line flattens budgetLine.usedAmount as budgetLine.usedAmount (nested), not at line root
      return this.line?.budgetLine?.usedAmount ?? this.pr.budgetUsedAmount ?? 0;
    },
    effectiveRemainingAmount() {
      return this.line?.budgetRemainingAmount ?? this.pr.budgetRemainingAmount;
    },
    effectiveRemainingAfterPR() {
      return this.line?.budgetRemainingAfterPR ?? this.pr.budgetRemainingAfterPR;
    },
    computedBudgetUsedPercent() {
      const allocated = Number(this.effectiveAllocated);
      const used = Number(this.effectiveUsedAmount);
      if (!allocated || allocated <= 0) return 0;
      const raw = (used / allocated) * 100;
      if (!Number.isFinite(raw)) return 0;
      return Math.max(0, Math.min(100, Math.round(raw)));
    },
    visibleAlerts() {
      const all = this.pr.budgetAlerts || [];
      if (!this.line) return all;
      return all.filter((a) => a.lineNumber === this.line.lineNumber);
    },
  },
  methods: {
    open() {
      this.$refs.budgetModal.show();
    },
    convertPrice,
    formatVnd(val) {
      const n = Number(val);
      if (!Number.isFinite(n)) return '--';
      return convertPrice(n);
    },
    formatShortVnd(val) {
      const n = Number(val);
      if (!Number.isFinite(n)) return '--';
      const abs = Math.abs(n);
      if (abs >= 1e9) return `${this.trimZeros((n / 1e9).toFixed(1))}B`;
      if (abs >= 1e6) return `${this.trimZeros((n / 1e6).toFixed(0))}M`;
      if (abs >= 1e3) return `${this.trimZeros((n / 1e3).toFixed(0))}K`;
      return `${this.trimZeros(n.toFixed(0))}`;
    },
    trimZeros(str) {
      return String(str).replace(/\.0$/, '');
    },
  },
};
</script>

<style scoped>
.pr-budget-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 12px;
}

.pr-budget-level {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pr-budget-level-tag {
  background: #e7f1ff;
  color: #4e7dd6;
  font-weight: 700;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 0.75rem;
}

.pr-budget-kpi-box {
  background: #ffffff;
  border-radius: 10px;
  padding: 16px 18px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.03);
}

.pr-budget-kpi-box--remaining {
  background: #eafaf4;
}

.pr-budget-kpi-box--remaining-danger {
  background: #fff5f5;
}

.pr-budget-kpi-big {
  font-size: 1.35rem;
  font-weight: 900;
  color: #111;
  margin-top: 10px;
  line-height: 1.1;
}

.pr-budget-kpi-sub {
  margin-top: 8px;
  font-size: 0.85rem;
  color: #6c757d;
}

.pr-budget-progress-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pr-budget-progress-percent {
  font-weight: 800;
  color: #2bb673;
}

.pr-budget-alert {
  background: #fff5f5;
  border-color: #f1c0c0;
  color: #6b1b1b;
}

.pr-budget-snapshots-amount {
  font-size: 1.05rem;
  font-weight: 900;
}
</style>
